import type { RootState } from '@/rootReducer';
import { sendAnalytics } from '@/services/analyticsService';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useIsScreenWidthSmallerThan } from '@/hooks/useIsScreenWidthSmallerThan';
import { useSequencesContext } from '../SequencesContext';
import type { StepsType } from './useSequences';

export function useSteps(step: StepsType) {
	const {
		setSelectedStep,
		selectedTemplate,
		setSelectedTemplate,
		selectedSequence,
		setSelectedSequence
	} = useSequencesContext();

	const screenSize = useIsScreenWidthSmallerThan(1024);

	const user = useSelector((state: RootState) =>
		state.user.getIn(['authorize', 'user']).toJS()
	);

	function handleRemoveStep() {
		if (!selectedTemplate || !selectedSequence) return;

		const newSteps = selectedTemplate?.steps.filter(s => s.id !== step.id);
		const stepsReordered = newSteps.map((s, i) => {
			return {
				...s,
				order: i
			};
		});
		setSelectedTemplate({
			...selectedTemplate,
			steps: stepsReordered
		});
		setSelectedSequence({
			...selectedSequence,
			steps: stepsReordered
		});

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: selectedSequence.id,
			step,
			eventName: 'User removed step from sequence'
		});
	}

	function handleMoveStepDown() {
		if (!selectedTemplate || !selectedSequence) return;

		const newSteps = selectedTemplate?.steps.map(s => {
			if (s.order === step.order) {
				return {
					...s,
					order: s.order + 1
				};
			}
			if (s.order === step.order + 1) {
				return {
					...s,
					order: s.order - 1
				};
			}
			return s;
		});
		setSelectedTemplate({
			...selectedTemplate,
			steps: newSteps
		});
		setSelectedSequence({
			...selectedSequence,
			steps: newSteps
		});

		setSelectedStep({ ...step, order: step.order + 1 });

		if (screenSize.isSmaller) {
			setSelectedStep(undefined);
		}

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: selectedSequence.id,
			step,
			eventName: 'User moved step down in sequence'
		});
	}

	function handleMoveStepUp() {
		if (!selectedTemplate || !selectedSequence) return;

		const newSteps = selectedTemplate?.steps.map(s => {
			if (s.order === step.order) {
				return {
					...s,
					order: s.order - 1
				};
			}
			if (s.order === step.order - 1) {
				return {
					...s,
					order: s.order + 1
				};
			}
			return s;
		});
		setSelectedTemplate({
			...selectedTemplate,
			steps: newSteps
		});
		setSelectedSequence({
			...selectedSequence,
			steps: newSteps
		});

		setSelectedStep({ ...step, order: step.order - 1 });

		if (screenSize.isSmaller) {
			setSelectedStep(undefined);
		}

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: selectedSequence.id,
			step,
			eventName: 'User moved step up in sequence'
		});
	}

	function handleDuplicateStep() {
		if (!selectedTemplate || !selectedSequence) return;

		const duplicatedStep = {
			...step,
			sendAfterInterval: step.order === 0 ? 1 : step.sendAfterInterval,
			order: selectedTemplate?.steps.length,
			id: uuidv4()
		};
		const newSteps = [...selectedTemplate?.steps, duplicatedStep];
		setSelectedTemplate({
			...selectedTemplate,
			steps: newSteps
		});
		setSelectedSequence({
			...selectedSequence,
			steps: newSteps
		});
		setSelectedStep(duplicatedStep);

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: selectedSequence.id,
			duplicatedStep,
			eventName: 'User duplicated sequence step'
		});
	}

	const stepDropdownMenuOptions = [
		{
			id: 'duplicate',
			label: 'Duplicate',
			onClick: handleDuplicateStep
		},
		{
			id: 'moveup',
			label: 'Move Up',
			onClick: handleMoveStepUp,
			disabled: step.order === 0 || step.order === 1
		},
		{
			id: 'movedown',
			label: 'Move Down',
			onClick: handleMoveStepDown,
			disabled:
				step.order === 0 ||
				step.order === (selectedTemplate?.steps.length || 0) - 1
		},
		{
			id: 'remove',
			label: 'Remove',
			onClick: handleRemoveStep,
			disabled: step.order === 0
		}
	];

	return {
		stepDropdownMenuOptions
	};
}
